import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Select, Switch, Table, Tag, message } from 'antd';
import { CheckOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  RESOURCE_TYPE_LABEL,
  adminPermissionList
} from '../permissions/permissionManageData';
import type { AdminPermissionGroup, GroupPermissionItem } from './permissionGroupManageData';

type PermissionGroupDetailModalProps = {
  open: boolean;
  group: AdminPermissionGroup | null;
  onClose: () => void;
  onSave: (groupId: string, permissions: GroupPermissionItem[], enabled: boolean) => void;
};

export function PermissionGroupDetailModal({
  open,
  group,
  onClose,
  onSave
}: PermissionGroupDetailModalProps) {
  const [enabled, setEnabled] = useState(true);
  const [permissions, setPermissions] = useState<GroupPermissionItem[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedIds, setPickedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !group) return;
    setEnabled(group.enabled);
    setPermissions(group.permissions.map((p) => ({ ...p })));
    setPickerOpen(false);
    setPickedIds([]);
  }, [open, group]);

  const availableOptions = useMemo(() => {
    const used = new Set(permissions.map((p) => p.id));
    return adminPermissionList
      .filter((p) => !used.has(p.id))
      .map((p) => ({ value: p.id, label: `${p.name}（${RESOURCE_TYPE_LABEL[p.resourceType]}）` }));
  }, [permissions]);

  const toggleItemEnabled = (id: string, checked: boolean) => {
    setPermissions((prev) =>
      prev.map((item) => (item.id === id ? { ...item, enabled: checked } : item))
    );
  };

  const removeItem = (id: string) => {
    setPermissions((prev) => prev.filter((item) => item.id !== id));
  };

  const confirmAdd = () => {
    if (pickedIds.length === 0) {
      message.warning('请选择要添加的功能');
      return;
    }
    const next = pickedIds
      .map((id) => adminPermissionList.find((p) => p.id === id))
      .filter(Boolean)
      .map((p) => ({
        id: p!.id,
        name: p!.name,
        resourceType: p!.resourceType,
        resourceValue: p!.resourceValue,
        description: p!.description,
        enabled: p!.enabled
      }));
    setPermissions((prev) => [...prev, ...next]);
    setPickerOpen(false);
    setPickedIds([]);
    message.success(`已添加 ${next.length} 项功能`);
  };

  const handleSave = () => {
    if (!group) return;
    onSave(group.id, permissions, enabled);
  };

  const columns: ColumnsType<GroupPermissionItem> = [
    {
      title: '功能名称',
      dataIndex: 'name',
      width: 220,
      ellipsis: true,
      render: (v: string) => <span className='admin-cell-primary'>{v}</span>
    },
    {
      title: '功能标识',
      dataIndex: 'resourceValue',
      width: 220,
      ellipsis: true,
      render: (v: string | null | undefined) =>
        v ? (
          <code className='admin-dict-code'>{v}</code>
        ) : (
          <span className='text-[#94a3b8]'>未配置</span>
        )
    },
    {
      title: '启用状态',
      dataIndex: 'enabled',
      width: 100,
      align: 'center',
      render: (checked: boolean) => <Switch size='small' checked={checked} disabled />
    }
  ];

  return (
    <>
      <Modal
        title={
          <div className='admin-pg-detail-title'>
            <span>分组详情</span>
          </div>
        }
        open={open}
        onCancel={onClose}
        destroyOnClose
        centered
        width={920}
        className='admin-pg-detail-modal'
        footer={null}
      >
        {group ? (
          <div className='admin-pg-detail-body'>
            <div className='admin-pg-detail-meta'>
              <div className='admin-pg-detail-meta-main'>
                <div className='admin-pg-detail-name-row'>
                  <h3 className='admin-pg-detail-name'>{group.name}</h3>
                </div>
                <p className='admin-pg-detail-desc'>分组标识：{group.businessCode || '—'}</p>
              </div>
              <div className='admin-pg-detail-status'>
                <Switch checked={enabled} disabled={true} />

              </div>
            </div>

            <div className='admin-pg-detail-section'>
              <div className='admin-pg-detail-section-head'>
                <div className='admin-pg-detail-section-title'>功能列表</div>
                <div className='admin-pg-detail-section-actions'>
                  <span className='admin-pg-detail-count'>共 {permissions.length} 项功能</span>

                </div>
              </div>

              <Table<GroupPermissionItem>
                rowKey='id'
                columns={columns}
                dataSource={permissions}
                pagination={false}
                size='middle'
                className='admin-user-table admin-pg-detail-table'
                locale={{ emptyText: '暂无功能，请点击新增功能' }}
                scroll={{ x: 800 }}
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        title='新增功能'
        open={pickerOpen}
        onCancel={() => {
          setPickerOpen(false);
          setPickedIds([]);
        }}
        destroyOnClose
        centered
        width={480}
        footer={
          <div className='flex justify-end gap-2'>
            <Button
              className='rounded-lg'
              onClick={() => {
                setPickerOpen(false);
                setPickedIds([]);
              }}
            >
              取消
            </Button>
            <Button type='primary' className='wb-btn-primary rounded-lg' onClick={confirmAdd}>
              确认
            </Button>
          </div>
        }
      >
        <Select
          mode='multiple'
          allowClear
          showSearch
          optionFilterProp='label'
          className='w-full'
          placeholder='请选择功能权限'
          options={availableOptions}
          value={pickedIds}
          onChange={setPickedIds}
        />
      </Modal>
    </>
  );
}
